'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Alert, ShieldCheck } from '@repo/ui/icons';
import { WarningIcon } from '@/components/common/WarningIcon';
import type { CapChangeNoticeRow, CapChangeNoticeTimelockEntry } from './CapChangeNotice.utils';
import { getPendingTimelocksForFunction, type VaultV2PendingTimelockChange, type VaultV2TimelocksWarning } from '../utils';
import VaultV2PendingTimelocksIndicator, { type VaultV2TimelockDuration } from '../VaultV2PendingTimelocksIndicator';
import { getCapChangeNoticeState } from './CapChangeNotice.utils';
interface CapChangeNoticeOverviewProps {
  timelocks: CapChangeNoticeTimelockEntry[];
  warning: VaultV2TimelocksWarning;
  pendingTimelocks: VaultV2PendingTimelockChange[];
  indicatorTimelocks: VaultV2TimelockDuration[];
}
const ROW_TOOLTIPS: Record<CapChangeNoticeRow['functionName'], string> = {
  addAdapter: 'Adding an adapter can expand where vault assets are supplied. This timelock is the review window before lender funds can be allocated through a newly enabled adapter.',
  increaseAbsoluteCap: 'Increasing the absolute cap lets the curator supply more total assets to one allocation. For lenders, this can increase how much of the vault is exposed there.',
  increaseRelativeCap: 'Increasing the relative cap lets the curator supply a larger share of the vault to one allocation. For lenders, this can increase concentration as the vault grows.'
};
const StatusIcon = t0 => {
  const $ = _c(7);
  const {
    row
  } = t0;
  if (row.status === "abdicated") {
    return null;
  }
  if (row.status === "belowMinimum") {
    let t1;
    if ($[0] !== row.warning) {
      t1 = row.warning ? <span data-testid="vault-v2-timelock-below-minimum"><WarningIcon warnings={[row.warning]} isSimple={true} /></span> : null;
      $[0] = row.warning;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.status === "meetsTarget") {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <TooltipTrigger asChild={true}><Icon icon={<ShieldCheck />} className="text-success-foreground size-5" aria-hidden={true} /></TooltipTrigger>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== row.iconTooltip) {
      t2 = <Tooltip>{t1}<TooltipContent>{row.iconTooltip}</TooltipContent></Tooltip>;
      $[3] = row.iconTooltip;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (row.status === "belowTarget") {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Icon icon={<Alert />} className="text-secondary-foreground size-5" aria-hidden={true} />;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={<Alert />} className="text-destructive-foreground size-5" aria-hidden={true} />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const CapChangeNoticeOverviewRow = t0 => {
  const $ = _c(34);
  const {
    row,
    pendingTimelocks,
    indicatorTimelocks
  } = t0;
  let t1;
  if ($[0] !== pendingTimelocks || $[1] !== row.functionName) {
    t1 = getPendingTimelocksForFunction(pendingTimelocks, row.functionName);
    $[0] = pendingTimelocks;
    $[1] = row.functionName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matchingPendingTimelocks = t1;
  let t2;
  if ($[3] !== row.label) {
    t2 = <span className="text-foreground text-xs">{row.label}</span>;
    $[3] = row.label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = ROW_TOOLTIPS[row.functionName];
  let t4;
  if ($[5] !== t3) {
    t4 = <InfoTooltip tooltipContent={t3} className="text-secondary-foreground hover:text-foreground size-5 transition-colors" />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div className="gap-2xs flex items-center">{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== row.description) {
    t6 = <span className="text-2xs text-secondary-foreground">{row.description}</span>;
    $[10] = row.description;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="gap-2xs flex min-w-0 flex-col">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== indicatorTimelocks || $[16] !== matchingPendingTimelocks) {
    t8 = matchingPendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={matchingPendingTimelocks} timelocks={indicatorTimelocks} />;
    $[15] = indicatorTimelocks;
    $[16] = matchingPendingTimelocks;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== row) {
    t9 = <StatusIcon row={row} />;
    $[18] = row;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== row.formattedDuration) {
    t10 = <span className="text-foreground text-xs whitespace-nowrap">{row.formattedDuration}</span>;
    $[20] = row.formattedDuration;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = <div className="gap-2xs flex items-center">{t8}{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== row.statusNote) {
    t12 = row.statusNote && <span className="text-2xs text-secondary-foreground text-right">{row.statusNote}</span>;
    $[26] = row.statusNote;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <div className="gap-2xs flex shrink-0 flex-col items-end">{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t7) {
    t14 = <div className="gap-md border-border py-xs flex min-h-14 items-start justify-between border-b-[0.5px] last:border-b-0">{t7}{t13}</div>;
    $[31] = t13;
    $[32] = t7;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
const CapChangeNoticeOverview = t0 => {
  const $ = _c(10);
  const {
    timelocks,
    warning,
    pendingTimelocks,
    indicatorTimelocks
  } = t0;
  let t1;
  if ($[0] !== indicatorTimelocks || $[1] !== pendingTimelocks || $[2] !== timelocks || $[3] !== warning) {
    const notice = getCapChangeNoticeState(timelocks, warning);
    let t2;
    if ($[5] !== indicatorTimelocks || $[6] !== pendingTimelocks) {
      t2 = row => <CapChangeNoticeOverviewRow key={row.functionName} row={row} pendingTimelocks={pendingTimelocks} indicatorTimelocks={indicatorTimelocks} />;
      $[5] = indicatorTimelocks;
      $[6] = pendingTimelocks;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = notice.rows.map(t2);
    $[0] = indicatorTimelocks;
    $[1] = pendingTimelocks;
    $[2] = timelocks;
    $[3] = warning;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <>{t1}</>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export default CapChangeNoticeOverview;